import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Receipt } from 'lucide-react'
import clsx from 'clsx'

import { api } from '@/lib/api'
import { config } from '@/lib/config'
import { plataCentavos } from '@/lib/formato'
import { useSesion } from '@/lib/sesion'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'

import { SelectorCaja } from './SelectorCaja'
import { DialogoTicket } from './DialogoTicket'
import { CajaCerrada } from './CajaCerrada'
import { useCobroSinDetalle } from './cobroSinDetalle'
import { useEnterConfirma } from './useEnterConfirma'
import {
  AvisoReferencia,
  BotonesMedio,
  CampoComprobante,
  CampoReferencia,
  CobroDividido,
  MontoTipeado,
  PanelEntregado,
  TecladoMonto,
} from './PiezasCobro'
import { mensajeDeCobro } from './mensajeDeCobro'
import type { CobroDirecto, EstadoTurno, MedioPagoPos, OpcionesPos, VentaRegistrada } from './tipos'

// La pantalla de caja del local que NO carga productos (§1.1,
// `pos.solo_cobro`).
//
// Es el local donde la balanza etiquetadora hace toda la cuenta y emite el
// ticket: el sistema no vende, registra lo que entró. Con la pantalla del POS
// normal esa operatoria costaba abrir un diálogo con F3 en CADA venta —un paso
// de más, repetido todo el día— y encima dejaba a la vista media pantalla que
// ese local no usa: la grilla de productos, el lector, la balanza y el ticket
// de renglones.
//
// Por qué es una pantalla APARTE y no el POS con la mitad escondida: el POS
// pide catálogo, promociones y peso de la balanza, arma dos mapas de códigos y
// mantiene el ticket. Esconder todo eso con condicionales dejaría las consultas
// corriendo igual y el archivo lleno de ramas muertas; y lo que de verdad se
// comparte —las reglas del cobro— está compartido de verdad, en
// `useCobroSinDetalle` y `PiezasCobro`.
//
// Lo que NO cambia: el turno sigue siendo obligatorio, el permiso sigue
// decidiendo quién cobra, el fiado sigue afuera, y el ticket y su parámetro son
// los mismos. Los movimientos del cajón y el cierre siguen en la pantalla
// Turno, que es donde estaban.
export function CajaSoloCobro() {
  useAyudaDe('pos.solo_cobro')

  const qc = useQueryClient()
  const { usuario, puede } = useSesion()
  const capacidades = config().capacidades

  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emitida, setEmitida] = useState<VentaRegistrada | null>(null)

  // El último cobro, cuando el local no quiere el ticket en pantalla. Va con el
  // VUELTO, que es el único dato de esa pantalla que después no se puede
  // consultar en ningún lado.
  const [ultima, setUltima] = useState<VentaRegistrada | null>(null)

  const medios = useQuery({
    queryKey: ['pos', 'medios-pago'],
    queryFn: () => api.get<MedioPagoPos[]>('/medios-pago'),
    staleTime: 5 * 60_000,
  })

  const opciones = useQuery({
    queryKey: ['pos', 'opciones'],
    queryFn: () => api.get<OpcionesPos>('/pos/opciones'),
    retry: false,
    staleTime: 5 * 60_000,
  })

  // El turno se consulta para saber si la caja está abierta, y para nada más.
  //
  // Esta pantalla NO muestra lo que se lleva cobrado en el turno, y es una
  // decisión y no un olvido: el monitor del mostrador lo mira el cliente que
  // está pagando, así que un cuadro con lo que entró en el día le dice a
  // cualquiera cuánta plata hay en el local —y quien lo lee no es siempre el
  // que viene a comprar—. Lo cobrado del turno se ve en la pantalla Turno, que
  // se abre a propósito y de espaldas al mostrador. Es la misma familia que el
  // arqueo a ciegas: hay números que el sistema tiene y que esta pantalla no
  // puede mostrar.
  //
  // Se sigue refrescando solo porque el turno lo puede cerrar otra persona
  // desde Gestión, y ahí esta pantalla tiene que dejar de cobrar.
  const turno = useQuery({
    queryKey: ['pos', 'turno'],
    queryFn: () => api.get<EstadoTurno>('/turno'),
    refetchInterval: 30_000,
  })

  const sinTurno = turno.data?.abierto === false

  const registrar = async (datos: CobroDirecto): Promise<boolean> => {
    setEnviando(true)
    setError(null)
    setUltima(null)

    try {
      // Por el MISMO endpoint que una venta con renglones, igual que el
      // diálogo: es una venta —suma al turno, al talonario, al arqueo y al
      // reporte de medios de pago—, lo único que no tiene es el detalle. Un
      // endpoint propio sería duplicar el turno, la numeración, el ticket y la
      // idempotencia.
      const venta = await api.post<VentaRegistrada>('/ventas', {
        ...datos,
        esCobroDirecto: true,
        lineas: [],
        imprimir: capacidades.impresora,
      })

      // Ausente cuenta como SÍ, por lo mismo que en el POS: el parámetro se
      // agregó después y una caja que todavía no sincronizó no puede quedarse
      // sin el papel.
      if (opciones.data?.mostrarTicket === false) setUltima(venta)
      else setEmitida(venta)

      // El turno quedó con una venta más. Acá no se muestra ninguno de esos
      // números, pero la consulta es la MISMA que usa la pantalla Turno, así
      // que invalidarla es lo que hace que el arqueo esté al día cuando el
      // encargado la abre.
      void qc.invalidateQueries({ queryKey: ['pos', 'turno'] })

      return true
    } catch (e) {
      setError(mensajeDeCobro(e, 'No se pudo registrar el cobro. Reintentá.'))
      return false
    } finally {
      setEnviando(false)
    }
  }

  const refMonto = useRef<HTMLInputElement>(null)

  const c = useCobroSinDetalle({
    medios: medios.data ?? [],
    onCobrar: registrar,
    cobrando: enviando,
  })

  useEffect(() => {
    refMonto.current?.focus()
  }, [])

  /**
   * Cobra y deja la pantalla lista para el cliente siguiente.
   *
   * Limpiar SOLO cuando quedó registrado, y esa es la parte delicada: la
   * pantalla no se desmonta después de cobrar, así que limpiar cuando el
   * servidor rechazó perdería el monto tipeado y —peor— renovaría la clave de
   * idempotencia, con lo que el reintento entraría como un cobro nuevo y la
   * plata se registraría dos veces.
   */
  const cobrarYLimpiar = async () => {
    const ok = await c.cobrar()
    if (!ok) return

    c.reiniciar()
    refMonto.current?.focus()
  }

  // Enter cobra. Y es la ÚNICA tecla que cobra: F2 sería la del POS normal y no
  // se puede usar acá, porque un medio de pago puede tener F2 como tecla rápida
  // (el servidor acepta de F2 a F12) y las dos se pelearían por la misma tecla.
  useEnterConfirma(cobrarYLimpiar, { activo: !c.confirmando && !emitida })

  // ── Puertas ──

  if (sinTurno) {
    return (
      <Pantalla>
        <div className="grid flex-1 place-items-center">
          <CajaCerrada />
        </div>
      </Pantalla>
    )
  }

  if (!puede('venta.cobro_directo')) {
    return (
      <Pantalla>
        <div className="grid flex-1 place-items-center">
          <div className="max-w-[420px] space-y-2 text-center">
            <AlertCircle className="mx-auto h-7 w-7 text-ambar" />
            <p className="text-[17px] font-semibold text-tinta">No podés cobrar desde acá</p>

            <p className="text-[13.5px] leading-relaxed text-tinta-tenue">
              A tu usuario le falta el permiso <strong>Cobrar sin detallar productos</strong>, que
              es el que habilita esta pantalla. Lo agrega el dueño desde Configuración → Roles.
            </p>
          </div>
        </div>
      </Pantalla>
    )
  }

  return (
    <Pantalla>
      {/* En un teléfono las dos columnas se apilan y scrollea la PÁGINA: con
          cada columna scrolleando por su cuenta, el teclado quedaba dentro de
          una caja de doscientos píxeles y no se podía tocar. Desde una tablet
          para arriba son dos columnas independientes, y ahí el pie con el botón
          que cobra queda clavado abajo.
          El corte es `md` y no `lg` a propósito: una tablet en vertical —que es
          lo que hay en muchos mostradores— mide 768 px y con `lg` caía del lado
          del teléfono. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto md:flex-row md:overflow-hidden">
        {/* Izquierda: el monto y el teclado, grandes. Es lo único que se toca
            en la operación del día. */}
        <section className="flex flex-col gap-3 md:min-h-0 md:flex-1 md:overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2">
            <SelectorCaja compacto />

            <p className="flex flex-1 items-start gap-2 rounded-lg bg-crema-alt px-3 py-2 text-[12.5px] text-tinta-suave">
              <Receipt className="mt-px h-4 w-4 shrink-0 text-tinta-tenue" />
              Se registra cuánto entró y con qué medio. El detalle de la mercadería lo tiene el
              ticket que emitió la balanza.
            </p>
          </div>

          {/* El teclado carga UNA cosa a la vez y el display dice cuál: el total
              del ticket, o el importe del pago que se está agregando a la
              división. Antes decía «Monto del ticket» siempre, así que
              dividiendo se tipeaba creyendo cargar el pago y se cambiaba el
              total. */}
          <MontoTipeado
            texto={c.tecleandoTexto}
            etiqueta={c.tecleandoEtiqueta}
            contexto={
              c.modo === 'dividido' ? (
                <>
                  Total del ticket{' '}
                  <strong className="num text-tinta">{plataCentavos(c.montoCentavos)}</strong>
                  {' · falta '}
                  <strong className="num text-rojo">{plataCentavos(c.falta)}</strong>
                </>
              ) : undefined
            }
            refInput={refMonto}
            grande
          />

          <TecladoMonto tecla={c.tecla} grande estirar />

          {/* El campo lo decide el local: donde la balanza imprime una etiqueta
              sin numerar es un renglón que el cajero saltea en cada cobro. */}
          {opciones.data?.cobroDirectoComprobante !== false && (
            <CampoComprobante
              id="comprobante-solo-cobro"
              valor={c.comprobante}
              onCambio={c.setComprobante}
            />
          )}
        </section>

        {/* Derecha: el medio, el vuelto y el botón. El botón va en el PIE de la
            tarjeta y no en el cuerpo, por lo mismo que en los diálogos: es la
            acción que mueve la plata y no puede quedar abajo del pliegue. */}
        <aside className="flex w-full flex-col rounded-xl border border-borde bg-papel md:min-h-0 md:w-[340px] md:overflow-hidden lg:w-[400px]">
          <header className="flex items-center justify-between gap-2 border-b border-borde px-3 py-2.5">
            <span className="text-[14px] font-semibold text-tinta">Cobrar</span>

            {usuario && (
              <span className="truncate text-[12.5px] text-tinta-tenue">{usuario.nombre}</span>
            )}
          </header>

          <div className="space-y-3 p-3 md:min-h-0 md:flex-1 md:overflow-y-auto">
            {/* Lo que se acaba de cobrar, cuando el ticket no se muestra. */}
            {ultima && (
              <div className="rounded-lg border border-verde/30 bg-verde-tenue px-3 py-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-verde-oscuro">
                    <Check className="h-4 w-4" />
                    Cobrado {plataCentavos(ultima.totalCentavos)}
                  </span>

                  <button
                    type="button"
                    onClick={() => setEmitida(ultima)}
                    className="btn-fantasma btn-sm text-verde-oscuro"
                  >
                    Ver el ticket
                  </button>
                </div>

                {ultima.vueltoCentavos != null && ultima.vueltoCentavos > 0 && (
                  <p className="mt-1 flex items-baseline justify-between gap-2">
                    <span className="text-[12.5px] font-medium uppercase tracking-wide text-verde-oscuro">
                      Vuelto
                    </span>

                    <span className="num text-[26px] font-bold leading-none text-verde-oscuro">
                      {plataCentavos(ultima.vueltoCentavos)}
                    </span>
                  </p>
                )}
              </div>
            )}

            <BotonesMedio
              cobrables={c.cobrables}
              medio={c.medio}
              onElegir={c.elegirMedio}
              grande
            />

            {c.medio?.pideReferencia && (
              <CampoReferencia
                id="referencia-solo-cobro"
                valor={c.referencia}
                onCambio={c.setReferencia}
              />
            )}

            {c.faltaReferencia && <AvisoReferencia medio={c.medio} />}

            {/* Dividir el cobro es la EXCEPCIÓN, así que no ocupa lugar hasta
                que hace falta: mientras no se pidió es un enlace discreto, y
                recién ahí se abre el panel. Un panel permanente para algo que
                pasa una vez por semana empuja el vuelto —que se mira en cada
                venta— más abajo. */}
            {c.modo === 'dividido' ? (
              <CobroDividido
                pagos={c.pagos}
                onQuitar={c.quitar}
                onAgregar={c.agregar}
                onVolver={c.volverAUnMedio}
                puedeAgregar={c.puedeAgregar}
                motivoNoAgregar={c.motivoNoAgregar}
                importeAAgregar={c.importeAAgregar}
                pagoCentavos={c.pagoCentavos}
                falta={c.falta}
                medio={c.medio}
              />
            ) : (
              // Con un solo medio cobrable no hay nada que dividir, así que el
              // enlace no aparece: un camino que no lleva a ninguna parte es
              // peor que no tenerlo.
              c.montoCentavos > 0 &&
              c.cobrables.length > 1 && (
                <button
                  type="button"
                  onClick={c.dividir}
                  className="self-start text-[12.5px] text-tinta-tenue underline decoration-borde-fuerte underline-offset-2 hover:text-verde-oscuro"
                >
                  Dividir en dos o más medios de pago
                </button>
              )
            )}

            {c.efectivo && (
              <PanelEntregado
                id="entregado-solo-cobro"
                entregadoTexto={c.entregadoTexto}
                montoCentavos={c.montoCentavos}
                entregado={c.entregado}
                onCambio={c.setEntregado}
                vuelto={c.vuelto}
                grande
              />
            )}

            {error && (
              <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
          </div>

          <footer className="border-t border-borde bg-crema-alt px-3 py-3">
            <div className="mb-2.5 flex items-baseline justify-between gap-2">
              <span className="text-[13px] font-medium uppercase tracking-wide text-tinta-tenue">
                A cobrar
              </span>

              <span className="num text-[30px] font-bold leading-none text-tinta">
                {plataCentavos(c.montoCentavos)}
              </span>
            </div>

            <button
              type="button"
              onClick={() => void cobrarYLimpiar()}
              disabled={!c.listo || enviando}
              className={clsx('btn-primario w-full py-4 text-[17px]')}
            >
              {enviando ? 'Registrando…' : 'Cobrar'}
            </button>

            <p className="mt-1.5 text-center text-[12px] text-tinta-tenue">
              Enter también cobra, y pide confirmar antes de registrar.
            </p>
          </footer>
        </aside>
      </div>

      {emitida && (
        <DialogoTicket
          venta={emitida}
          onReimprimir={async () => {
            try {
              await api.post('/impresora/ticket', { ulid: emitida.ulid })
            } catch {
              // Si no salió el papel no hay nada que hacer desde acá: el cobro
              // está registrado y el ticket sigue en pantalla.
            }
          }}
          onCerrar={() => {
            setEmitida(null)
            refMonto.current?.focus()
          }}
        />
      )}
    </Pantalla>
  )
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full min-h-0 flex-col p-3">{children}</div>
}
